import { AssumptionEvidence } from '@/services/bayesianApi/models'
import {
  Interval,
  NetworkTransitionDefaults,
  TransitionFunction,
} from '@/types/variable'
import {
  buildTransitionStateProbabilities,
  resolveDefaultHalfBandPercent,
  resolveTransitionFunctionsForIntervals,
} from '@/utils/network/transitionFunctions'

// The minimal variable shape the derivation needs, structural rather than
// `Variable` so callers holding different shapes can satisfy it -- Marvin's paths
// hold `VariableFromBackend`, and KBE-840's wire path holds no variable row at all,
// only the node projection plus its measurement config.
//
// `stateCount` rather than `stateIds` because the COUNT is all this uses (the guard
// that the curve produced one probability per state). A caller holding a real
// variable goes through `transitionEvidenceVariableFor`; one holding a wire node
// already has the count.
export type TransitionEvidenceVariable = {
  // Only used to name the variable when corrupt stored data is reported.
  name?: string
  stateCount: number
  intervals?: Interval[] | null
  transitionFunctions?: TransitionFunction[] | null
}

// The one conversion from a variable row, so no caller counts states by hand.
export const transitionEvidenceVariableFor = (
  variable:
    | {
        name?: string
        stateIds: string[]
        intervals?: Interval[] | null
        transitionFunctions?: TransitionFunction[] | null
      }
    | undefined,
): TransitionEvidenceVariable | undefined =>
  variable && {
    name: variable.name,
    stateCount: variable.stateIds.length,
    intervals: variable.intervals,
    transitionFunctions: variable.transitionFunctions,
  }

export type VirtualEvidenceInput = {
  // Accepts a string as well as a number because the two assumption models
  // disagree on the representation of the same `assumption.interval_value`
  // column: `services/models/assumptionSet/assumptions.ts` converts it to a
  // number, while `services/db/models/AssumptionSetAggregate.ts` keeps Drizzle's
  // raw numeric-as-string. Normalizing in this one funnel means neither caller
  // has to remember which it holds -- and a caller that passes the wrong one
  // degrades to hard evidence rather than to NaN.
  intervalValue: number | string | null | undefined
  variable: TransitionEvidenceVariable | undefined
  network: NetworkTransitionDefaults | null | undefined
  // Refuse to compute on corrupt stored transition data rather than answering with
  // substituted values (KBE-839). Off by default: the render callers TRUST the
  // database, because no write path can produce such a row, they only display a
  // number a researcher is looking at, and nothing drawn into the app shell may
  // throw anyway (gotcha 27). The callers that SUBMIT work off this evidence pass
  // true -- an hours-long optimization job that writes CPTs back, and a Marvin
  // answer reported out-of-band, are both too expensive to get quietly wrong.
  refuseUntrustworthyData?: boolean
}

// The single definition of "a usable numeric assumption value", shared with
// callers that need the number itself rather than a vector (Marvin reports the
// applied value back to the model). Returns undefined for absent, blank, or
// unparseable input -- `Number('')` is 0, which would otherwise read as a real
// measurement sitting in whichever interval claims zero.
export function normalizeIntervalValue(
  intervalValue: number | string | null | undefined,
): number | undefined {
  if (intervalValue === null || intervalValue === undefined) return undefined
  if (typeof intervalValue === 'string' && intervalValue.trim() === '') {
    return undefined
  }
  const numericIntervalValue = Number(intervalValue)
  return Number.isFinite(numericIntervalValue)
    ? numericIntervalValue
    : undefined
}

// KBE-839: unlike the editor, which resolves its own unsaved state, every variable
// reaching this funnel came out of the database -- where the one-config-per-boundary
// alignment is upheld by every write path and by nothing below them (no DB
// constraint; one was written and rejected, see the architecture-decisions record).
// So a misaligned array arriving here means a writer bypassed the invariant or
// someone ran a hand-written UPDATE.
//
// Neither shape announces itself: a stale length resolves to the network default,
// and a missing point to a hard cutoff (the resolved band is NaN, so every `> 0`
// guard downstream reads false and the vector looks perfectly ordinary).
//
// This is what is wrong with a stored array, or null when nothing is. Not exported:
// callers ask the funnel to refuse, they do not inspect the verdict themselves.
function describeUntrustworthyTransitionFunctions(
  variable: Pick<
    TransitionEvidenceVariable,
    'name' | 'intervals' | 'transitionFunctions'
  >,
): string | null {
  const stored = variable.transitionFunctions
  if (!stored) return null
  const boundaryCount = Math.max((variable.intervals?.length ?? 0) - 1, 0)
  if (stored.length !== boundaryCount) {
    return `${stored.length} stored transition configs against ${boundaryCount} boundaries; the stored tuning is unreadable and resolves to the network default instead`
  }
  if (
    stored.some(
      (entry) =>
        !Number.isFinite(entry?.midpoint) ||
        !Number.isFinite(entry?.fivePercentValue),
    )
  ) {
    return 'a stored transition config has no finite midpoint or fivePercentValue; that boundary resolves to a hard cutoff rather than to the stored tuning'
  }
  return null
}

// Applied only once the funnel has decided it will actually build a vector, which is
// what keeps the refusal scoped: for a hard state assumption carrying no numeric
// value, the stored configs are never read and there is nothing to refuse. Checking
// at the submit call sites instead (as an earlier revision did) ran ahead of those
// early returns and aborted an optimize job over data it would never have consulted.
//
// Reachable despite every write path maintaining the invariant, which is why this is
// not dead code: KBE-841 -- `resolved_variable` composes a delta branch's `intervals`
// and `transition_functions` independently against a LIVE base, so it can hand this
// funnel a misaligned pair while both stored rows are individually well-formed. No
// row-level constraint can see that, and it goes live with the reader swap.
//
// Exported for the local engine (KBE-840), which builds a measurement scale rather
// than a vector and so cannot reach the refusal through `buildVirtualEvidence`. That
// caller still ASKS to be refused rather than inspecting the verdict, which is the
// distinction that keeps `describeUntrustworthyTransitionFunctions` private.
export function refuseUntrustworthyTransitionFunctions(
  variable: Pick<
    TransitionEvidenceVariable,
    'name' | 'intervals' | 'transitionFunctions'
  >,
): void {
  const problem = describeUntrustworthyTransitionFunctions(variable)
  if (!problem) return
  throw new Error(
    `Variable "${variable.name ?? 'unknown'}": ${problem}. Refusing to compute rather than answer with substituted values -- clear the variable's transition functions to inherit the network default, or restore a valid set. See KBE-839.`,
  )
}

// KBE-774: the ONE place a researcher's numeric assumption value becomes a
// virtual-evidence probability vector. Every calculation path (diagnosis,
// requirement statuses, optimization, Marvin inference, network report) routes
// through here, so none of them can drift from the curve the editor chart
// draws -- the failure KBE-774 existed to fix was five paths each snapping the
// value to a hard state index on their own.
//
// KBE-769: this applies to EVERY semi-continuous variable, not only ones
// carrying their own configs -- a variable with no stored `transitionFunctions`
// inherits the network's default rather than opting out. There is no
// per-variable off switch; the network default of 0% (a hard cutoff) is the off
// switch.
//
// Returns undefined -- meaning "fall back to hard evidence" -- when no continuous
// value was entered, the variable is discrete, or its data is
// not in a buildable shape. A non-contiguous partition is a legitimately transient
// mid-edit state and degrades that way rather than throwing.
//
// It throws for corrupt stored transition data only when the caller passes
// `refuseUntrustworthyData`, and only after the early returns above, so a variable
// whose curve is never consulted is never refused.
export function buildVirtualEvidence({
  intervalValue,
  variable,
  network,
  refuseUntrustworthyData = false,
}: VirtualEvidenceInput): number[] | undefined {
  const numericIntervalValue = normalizeIntervalValue(intervalValue)
  if (numericIntervalValue === undefined) return undefined
  if (!variable?.intervals || variable.intervals.length === 0) return undefined
  if (refuseUntrustworthyData) {
    refuseUntrustworthyTransitionFunctions(variable)
  }
  // A value outside the variable's partition is deliberately NOT special-cased:
  // the curve is evaluated for it like any other, which saturates to the nearest
  // end state. Such a value can only exist as a leftover -- `setIntervalAssumption`
  // validates on write, but only `setVariableIntervals` refuses to strand an
  // existing one when the bounds move (`setIntervalAndAdjustEndings` and
  // `mergeIntervalChanges` do not). Applying it beats dropping it: the researcher
  // set that assumption deliberately, and `IntervalInput` already shows the value
  // in red with an explanatory tooltip, so the leftover is visible rather than
  // silently ignored.
  try {
    const probabilities = buildTransitionStateProbabilities(
      numericIntervalValue,
      variable.intervals,
      resolveTransitionFunctionsForIntervals(
        variable.transitionFunctions,
        resolveDefaultHalfBandPercent(network),
        variable.intervals,
      ),
    )
    return probabilities.length === variable.stateCount
      ? probabilities
      : undefined
  } catch {
    return undefined
  }
}

export type InferAssumptionEvidenceInput = VirtualEvidenceInput & {
  // The hard observed state index. `undefined` (or negative, which a caller may
  // produce from a failed `indexOf`) means unobserved.
  stateIndex: number | undefined
}

// A measured assumption carries no state (KBE-946), so a value the engine cannot
// read has nothing to fall back to. Dropping it would leave an assumption the
// researcher can see in the table out of the answer, with nothing in the logs, so
// both engines refuse instead. Stored data cannot reach this -- `changeToDiscrete`
// converts interval assumptions to states in the same write -- so it means a
// client sent a measurement against a scale it no longer has.
export function unreadableMeasurementError(
  variable: string,
  value: number,
): Error {
  return new Error(
    `Variable "${variable}" has the measured value ${value}, but no measurement scale to read it against (its intervals may have been removed). Remove the assumption and add it again.`,
  )
}

// Evidence for one variable in an infer/infers/diagnose request: the virtual
// vector when one can be derived, else the hard state index. Returns undefined
// for an unobserved assumption -- the infer contract has no representation for
// one, so those callers drop the entry. The optimize contract keeps them (as
// `observedState: null`) and therefore composes `buildVirtualEvidence` itself.
// Throws for a measured value with no state that cannot be read (see
// `unreadableMeasurementError`).
export function buildInferAssumptionEvidence({
  stateIndex,
  ...virtualEvidenceInput
}: InferAssumptionEvidenceInput): AssumptionEvidence | undefined {
  const virtualEvidence = buildVirtualEvidence(virtualEvidenceInput)
  if (virtualEvidence) return virtualEvidence
  if (stateIndex !== undefined && stateIndex >= 0) return stateIndex
  const measuredValue = normalizeIntervalValue(
    virtualEvidenceInput.intervalValue,
  )
  if (measuredValue !== undefined) {
    throw unreadableMeasurementError(
      virtualEvidenceInput.variable?.name ?? 'unknown',
      measuredValue,
    )
  }
  return undefined
}
